import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  LinearProgress,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useGlobalConfig } from 'context/GlobalContext';
import { MigrationApis } from 'apis/migrationAPI';
import {
  MigrationJob,
  MigrationJobStep,
  RecommendationMigrationValidationReport,
  MigrationValidationErrorsByStage,
} from 'types/apis/migrationInterfaces';

const VALIDATION_STAGE_LABELS: Record<keyof MigrationValidationErrorsByStage, string> = {
  homeProfileAttributes: 'Home profile attributes',
  applianceProfileDefaults: 'Appliance profile defaults',
  recoApplianceExclusions: 'Recommendation appliance exclusions',
  recommendationApplianceCategories: 'Recommendation categories (appliance-category)',
  recoScoreCategories: 'Reco score categories (master JSON)',
};

function ValidationReportDetails({ report }: { report: RecommendationMigrationValidationReport }) {
  const { errorsByStage, warnings } = report;
  const stages = (Object.keys(errorsByStage) as (keyof MigrationValidationErrorsByStage)[]).filter(
    (k) => errorsByStage[k].length > 0
  );

  return (
    <Box sx={{ mt: 1 }}>
      {warnings.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="warning.main" sx={{ mb: 0.5 }}>
            Warnings (from validation)
          </Typography>
          <List dense disablePadding>
            {warnings.map((w) => (
              <ListItem key={w} disableGutters sx={{ py: 0 }}>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={w} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      {stages.map((key) => (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
            {VALIDATION_STAGE_LABELS[key]}
          </Typography>
          <List dense disablePadding>
            {errorsByStage[key].map((line) => (
              <ListItem key={line} disableGutters sx={{ py: 0 }}>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={line} />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}

interface MigrationJobDetailsProps {
  job: MigrationJob;
  onBack: () => void;
  onComplete?: () => void;
  /** Status poll interval in ms. Default 30s; dry-runs pass a shorter value since they finish in seconds. */
  pollIntervalMs?: number;
  /** Label for the back button (default "Back to Jobs"). */
  backLabel?: string;
}

const STEP_LABELS: Record<string, string> = {
  PARSE: 'Parse CSV Files',
  TRANSFORM: 'Transform to CMS Format',
  IMPORT: 'Import Recommendations',
};

const getStepStatus = (step: MigrationJobStep) => {
  switch (step.status) {
    case 'COMPLETED':
      return { icon: <CheckCircleIcon color="success" />, color: 'success' as const };
    case 'FAILED':
      return { icon: <ErrorIcon color="error" />, color: 'error' as const };
    case 'RUNNING':
      return { icon: <PlayCircleIcon color="primary" />, color: 'primary' as const };
    default:
      return { icon: <HourglassEmptyIcon color="action" />, color: 'default' as const };
  }
};

export const MigrationJobDetails: React.FC<MigrationJobDetailsProps> = ({
  job: initialJob,
  onBack,
  onComplete,
  pollIntervalMs = 30_000,
  backLabel = 'Back to Jobs',
}) => {
  const { rc } = useGlobalConfig();
  const [job, setJob] = useState<MigrationJob>(initialJob);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const fetchJobStatus = useCallback(async () => {
    const resp = await MigrationApis.getJob(rc, job.jobId);
    if (resp.data) {
      setJob(resp.data);
      if (resp.data.jobStatus === 'COMPLETED' || resp.data.jobStatus === 'FAILED') {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        if (resp.data.jobStatus === 'COMPLETED') {
          onCompleteRef.current?.();
        }
      }
    }
  }, [rc, job.jobId]);

  useEffect(() => {
    void fetchJobStatus();
  }, [fetchJobStatus]);

  useEffect(() => {
    // Start polling if job is in progress
    if (job.jobStatus === 'RUNNING' || job.jobStatus === 'PENDING') {
      pollingRef.current = setInterval(fetchJobStatus, pollIntervalMs);
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [job.jobStatus, fetchJobStatus, pollIntervalMs]);

  const steps = job.jobs || [];
  const activeStep = steps.findIndex(s => s.status === 'RUNNING' || s.status === 'PENDING');
  const completedSteps = steps.filter(s => s.status === 'COMPLETED').length;

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        {backLabel}
      </Button>

      {/* Job Header */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={500}>{job.jobId}</Typography>
          <Chip
            label={job.jobStatus}
            color={
              job.jobStatus === 'COMPLETED' ? 'success' :
              job.jobStatus === 'FAILED' ? 'error' :
              job.jobStatus === 'RUNNING' ? 'primary' : 'default'
            }
          />
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Progress</Typography>
            <Typography variant="body2" fontWeight={500}>{job.progress}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={job.progress}
            sx={{ height: 8, borderRadius: 4 }}
            color={
              job.jobStatus === 'FAILED' ? 'error' :
              job.jobStatus === 'COMPLETED' ? 'success' : 'primary'
            }
          />
        </Box>

        {/* Error Alert */}
        {job.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Box>
              {job.error.code && <strong>[{job.error.code}]</strong>}{' '}
              {job.error.message || 'Unknown error'}
              {job.error.details && (
                <ValidationReportDetails report={job.error.details} />
              )}
            </Box>
          </Alert>
        )}

        {/* Stats - show from result.stats or from IMPORT step metadata for live updates */}
        {(() => {
          // Get stats from result or from IMPORT step metadata (for live updates)
          const importStep = steps.find(s => s.cType === 'IMPORT');
          const liveStats = importStep?.data?.[0]?.metadata;
          const finalStats = job.result?.stats;

          const stats = finalStats || (liveStats ? {
            totalRecommendations: liveStats.total || 0,
            created: liveStats.created || 0,
            updated: liveStats.updated || 0,
            failed: liveStats.failed || 0
          } : null);

          if (!stats) return null;

          const isLive = !finalStats && liveStats;

          return (
            <Grid container spacing={2}>
              <Grid size={{ xs: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" fontWeight={600}>
                    {isLive ? `${liveStats.processed}/${stats.totalRecommendations}` : stats.totalRecommendations}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{isLive ? 'Progress' : 'Total'}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
                  <Typography variant="h5" fontWeight={600}>{stats.created}</Typography>
                  <Typography variant="caption">Created</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.light', borderRadius: 1, color: 'info.contrastText' }}>
                  <Typography variant="h5" fontWeight={600}>{stats.updated}</Typography>
                  <Typography variant="caption">Updated</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: stats.failed > 0 ? 'error.light' : 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" fontWeight={600}>{stats.failed}</Typography>
                  <Typography variant="caption" color={stats.failed > 0 ? 'error.contrastText' : 'text.secondary'}>Failed</Typography>
                </Box>
              </Grid>
            </Grid>
          );
        })()}

        {job.jobStatus === 'COMPLETED' && job.result?.stats?.warnings && job.result.stats.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Import warnings</Typography>
            <List dense disablePadding>
              {job.result.stats.warnings.map((w) => (
                <ListItem key={w} disableGutters sx={{ py: 0 }}>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={w} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </Paper>

      {/* Steps */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Migration Steps</Typography>
      {steps.length > 0 ? (
        <Stepper activeStep={activeStep === -1 ? completedSteps : activeStep} orientation="vertical">
          {steps.map((step) => {
            const { icon, color } = getStepStatus(step);
            const metadata = step.data?.[0]?.metadata || {};
            return (
              <Step key={step.cType} completed={step.status === 'COMPLETED'}>
                <StepLabel StepIconComponent={() => icon}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {STEP_LABELS[step.cType] || step.cType}
                    <Chip label={step.status} size="small" color={color} variant="outlined" />
                  </Box>
                </StepLabel>
                <StepContent>
                  {Object.keys(metadata).length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: step.error ? 1 : 0 }}>
                      {Object.entries(metadata).map(([k, v]) => `${k}: ${v}`).join(' • ')}
                    </Typography>
                  )}
                  {step.error && (
                    <Alert severity="error" variant="outlined" sx={{ mt: 0.5 }}>
                      {step.error.code && <strong>[{step.error.code}]</strong>}{' '}
                      {step.error.message}
                    </Alert>
                  )}
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No step information available yet.
        </Typography>
      )}
    </Box>
  );
};

